<template>
  <div>
    <div style="margin-bottom: 20px;">
      <app-button
        :label="t('Adicionar Etapa de Produção')"
        icon="pi pi-plus"
        size="small"
        data-testid="so-production-steps-add-btn"
        @click="handleNew"
      />
    </div>

    <s-o-production-steps-table
      ref="soProductionStepsTableRef"
      @edit="handleEdit"
      @delete="handleDelete">
    </s-o-production-steps-table>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import SOProductionStepsTable from './SOProductionStepsTable.vue';
import { log } from '../../utils/DebugLogger';

type Id = number | string;

interface DataTableInstance {
  loadData: () => void;
}

const { t } = useI18n();

const soProductionStepsTableRef = ref<DataTableInstance | null>(null);

const loadData = async (): Promise<void> => {
  log('SOProductionStepsPage', 'loadData', 'Reloading data');
  if (soProductionStepsTableRef.value) {
    await soProductionStepsTableRef.value.loadData();
  }
};

const handleNew = (): void => {
  log('SOProductionStepsPage', 'handleNew', 'Opening modal for new production step');
  eventBus.emit('modal.so-production-steps.open', { action: 'create' });
};

const handleEdit = (id: Id): void => {
  log('SOProductionStepsPage', 'handleEdit', 'Editing production step', id);
  eventBus.emit('modal.so-production-steps.open', { id, action: 'edit' });
};

const handleDelete = (id: Id, data: Record<string, unknown> | null = null): void => {
  log('SOProductionStepsPage', 'handleDelete', 'Deleting production step', id);
  eventBus.emit('modal.so-production-steps.open', { id, data, action: 'delete' });
};

// Backward-compatible eventBus listeners (typed to match EventCallback shape)
const onBusNew = (): void => handleNew();
const onBusEdit = (data?: Id): void => {
  if (data !== undefined) handleEdit(data);
};
const onBusDelete = (data?: Id): void => {
  if (data !== undefined) handleDelete(data);
};

onMounted(() => {
  log('SOProductionStepsPage', 'mounted', 'Component mounted');
  eventBus.on('so-production-step.new', onBusNew);
  eventBus.on<Id>('so-production-step.edit', onBusEdit);
  eventBus.on<Id>('so-production-step.delete', onBusDelete);
});

onBeforeUnmount(() => {
  eventBus.off('so-production-step.new', onBusNew);
  eventBus.off<Id>('so-production-step.edit', onBusEdit);
  eventBus.off<Id>('so-production-step.delete', onBusDelete);
});

defineExpose({ loadData, handleNew, handleEdit, handleDelete });
</script>
